import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    AvailableTypeEnum,
    Field,
    getNonSavedFieldDefinitions,
    getSupportedItems,
    parseFieldsToMetadata,
    parseMetadataToFields,
    Type,
    typeListeners,
    useTypeValidationRules
} from './attributes';

import { useLazyQuery } from '@apollo/client';
import { UserContext } from '../common/auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { FormProvider } from '../common/form/FormContext';
import FormModal from '../common/form/FormModal';
import I18n, { useI18n } from '../common/i18n/I18n';
import { generateTempId } from '../common/utils/commonUtils';
import { client, termsetListQuery } from '../supply/termset/termset';
import { useFieldDefinitionData, useFieldDefinitionSaveWithAssociation, useFieldTypesData } from './attributesApi';
import { getFieldDefinitionFromField } from './fieldDefinitionAttributes';
import { FieldDefinitionCarousel } from './FieldDefinitionCarousel';
import { INITIAL_FORM_STATE } from './FieldEditor';
import FieldEditorForModal from './FieldEditorForModal';

export default ({
    open,
    setIsOpen,
    onClose,
    onSuccessfulAssociation,
    entity,
    fetchFromEntity,
    existingAttributes = [],
    entityId,
    setIsSavingAttributes = undefined,
    showMessageAfterAttributeAdd = true
}) => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const [selectedType, setSelectedType] = useState<Type>();
    const [typeClicked, setTypeClicked] = useState<Type>();
    const [selectedFields, setSelectedFields] = useState<Field[]>([]);
    const [value, setValue] = useState({ ...cloneDeep(INITIAL_FORM_STATE) });

    // two calls will be merged in one once we have active/inactive flag on field definition entity
    const { fieldDefinitionData, fieldDefinitionRefetch, isFieldDefinitionLoading } = useFieldDefinitionData(
        fetchFromEntity,
        'active'
    );

    const { saveAndAssociateFieldDefinition } = useFieldDefinitionSaveWithAssociation();
    const { fieldTypesData } = useFieldTypesData();
    const [isValid, setIsvalid] = useState(false);

    const { typeValidationRules } = useTypeValidationRules(selectedType, value);

    const validationCallback = (isValid) => {
        setIsvalid(isValid);
    };

    const fields = useMemo<Field[]>(() => {
        if (fieldDefinitionData) {
            const supportedFieldsOnClient = getSupportedItems(
                fieldDefinitionData?.results?.hits?.items.map((item) => {
                    return { ...item, fieldType: item.fieldType, description: item.about };
                })
            );
            const existingFieldMapped = getNonSavedFieldDefinitions(supportedFieldsOnClient, existingAttributes);

            return parseMetadataToFields(existingFieldMapped);
        }
        return [];
    }, [fieldDefinitionData, existingAttributes]);

    const types = useMemo(() => {
        if (fieldTypesData) {
            const fieldTypesMapped = getSupportedItems(
                fieldTypesData?.results?.hits?.items.map((item) => {
                    return { fieldType: item.id, description: item.about, typeName: item.name };
                })
            );
            return fieldTypesMapped.sort((a, b) => a.typeName.localeCompare(b.typeName));
        }
        return [];
    }, [fieldTypesData]);

    const onFieldDefinitionSave = (field: Field) => {
        //optimistic update with temp id
        const tempId = generateTempId().toString();
        const newFieldDefinition = getFieldDefinitionFromField(parseFieldsToMetadata(field), tempId);
        onSuccessfulAssociation([tempId], newFieldDefinition, true);
        saveAndAssociateFieldDefinition(
            newFieldDefinition,
            entity,
            entityId,
            showMessageAfterAttributeAdd,
            Boolean(tempId)
        )
            .then((res: any) => {
                if (res.data.addFieldDefinitionAssociation.errors) {
                    onSuccessfulAssociation([], undefined);
                } else {
                    setSelectedType(undefined);

                    //replace temp id with real id
                    const id = res.data.addFieldDefinitionAssociation.id;
                    const updatedFieldDefinition = getFieldDefinitionFromField(parseFieldsToMetadata(field), id);

                    onSuccessfulAssociation([id], updatedFieldDefinition, false);
                    fieldDefinitionRefetch();
                }
            })
            .catch(() => onSuccessfulAssociation([], undefined));
    };

    const [getData, { data: termsetsData }] = useLazyQuery(termsetListQuery, {
        client: client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: false
    });

    useEffect(() => {
        if (selectedType?.fieldType === AvailableTypeEnum.termset) {
            getData();
        }
    }, [selectedType, getData]);

    const listeners = typeListeners[selectedType?.fieldType];
    const title = selectedType ? (
        <I18n token="fieldDefinition.modal.addFieldDefinition.title" values={{ type: selectedType.typeName }} />
    ) : (
        <I18n token="fieldDefinition.modal.addAttributes.title" />
    );

    return (
        <FormModal
            width={'60%'}
            isOpen={open}
            onClose={onClose}
            title={title}
            id={`field-definition-modal-${entity}`}
            hideDefaultButtons={true}
            isLoading={isFieldDefinitionLoading}
            additionalActions={[
                {
                    label: selectedType ? useI18n('form.previous') : useI18n('dialog.cancel'),
                    onClick: () => {
                        selectedType || setIsOpen(false);
                        setSelectedType(undefined);
                        setValue({ ...cloneDeep(INITIAL_FORM_STATE) });
                    },
                    variant: 'text'
                },
                {
                    label: selectedFields.length || selectedType ? useI18n('form.add') : useI18n('form.next'),

                    onClick: () => {
                        if (selectedFields.length) {
                            setIsSavingAttributes?.(true);
                            const addedFieldDefs = selectedFields.map((f) => f.id);
                            onSuccessfulAssociation(addedFieldDefs);
                            return;
                        }

                        if (!selectedType && typeClicked) {
                            setSelectedType(typeClicked);
                            return;
                        }

                        //@ts-ignore
                        const newField: Field = {
                            ...cloneDeep(typeClicked),
                            ...cloneDeep(value)
                        };
                        if (newField && typeClicked) {
                            onFieldDefinitionSave(newField);
                        }
                    },
                    variant: 'contained',
                    disabled: (!typeClicked && !selectedFields.length) || (selectedType && !isValid)
                }
            ]}
        >
            {selectedType ? (
                <FormProvider
                    validationRules={typeValidationRules}
                    state={value}
                    setState={setValue}
                    listeners={listeners}
                    expandedSections={['attribute']}
                    allowMultipleExpansion={false}
                >
                    <FieldEditorForModal
                        type={selectedType}
                        termsetsData={termsetsData}
                        validationCallback={validationCallback}
                    />
                </FormProvider>
            ) : (
                <FieldDefinitionCarousel
                    types={types}
                    selectedFields={selectedFields}
                    setSelectedType={setSelectedType}
                    typeClicked={typeClicked}
                    setTypeClicked={setTypeClicked}
                    fields={fields}
                    setSelectedFields={setSelectedFields}
                    isInModal
                />
            )}
        </FormModal>
    );
};
